// Library methods
import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";

// MUI Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

// Components
import VisualAcuityGraph from "../../VisualAcuityGraph";
import BaileyLovieGraph from "../../VisualAcuityGraph/BaileyLovieGraph";
import NumberGraph from "../../NumberGraph";
import GazeGraph from "../../GazeGraph";
import EstermanGraph from "../../EstermanGraph";
import SingleThresholdGraph from "../../SingleThresholdGraph";
import TumblingE from "../../TumblingE";
import Synoptophore from "../../Synoptophore";
import PelliRobson from "../../PelliRobson";
import ColorVisionGraph from "../../ColorVisionGraph";
import { WebRtcComponent } from "../../WebRtc";
import ChartLegendExceptVisualField from "../../ChartLegendExceptVisualField";
import RAPDChartLegend from "../../RAPDChartLegend";
import RAPDChart from "../../RAPDChart";
import RAPDPupils from "../../RAPDPupils";

// Utilities
import { useLiveUpdateStatusContext } from "../../../../contexts/LiveUpdateStatusContext";
import { examCardEnums, eyeTypes } from "../../../../enums";
import { useLiveReportData } from "../../../../hooks/useLiveReporData";
import {
  getChartSpots,
  getGazeChartData,
} from "../../../../utils/examReportsHelper";

const LiveUpdateGraph = ({ connection }) => {
  // translations
  const { t } = useTranslation();

  const {
    exam,
    currentExamSection,
    isVrMode,
    isPaused,
    isConfirmation,
    isNotStarted,
    inTutorial,
    inCalibration,
    examReportLive,
    isRightCompleted,
    isVisualField,
    isEsterman,
    isVisualAcuity,
    isColorVision,
    isSynoptophore,
    isPelirobson,
    isTumbling,
    isLandoltC,
    isSnellen,
    isBaileyLovie,
    isSingleThreshold,
    isRAPD,
  } = useLiveUpdateStatusContext();

  // handling live data
  const { falsePositivePercentage, falseNegative, fixationLossPercentage } =
    useLiveReportData(examReportLive);

  const [, setshouldDisplayEyeTrackingError] = useState(false);

  const { eyeLeft, eyeRight } = eyeTypes;

  // the eye that the live update modal refers to (Left or Right)
  // the logic is:
  // - if there is only one section, then simply get the eye that it is related to
  // - if there are two section:
  //    - if right eye is not completed, then consider the right eye as the current one
  //    - if right eye is completed, then consider the left eye as the current one
  //    - to check if the section is completed, we should check if the completionDate field within the section, is null
  const eye = useMemo(() => {
    if (!exam) return "";

    // get the current exam section being tested
    const currentExamType = currentExamSection?.type;
    const enumValues = Object.values(examCardEnums);
    const currentSectionName = enumValues.find(
      (value) => value.type === currentExamType
    )?.sectionName;

    if (!exam?.[currentSectionName]?.length) return "";

    const rightSection = exam[currentSectionName].find(
      (section) => section.eye === eyeRight
    );
    const leftSection = exam[currentSectionName].find(
      (section) => section.eye === eyeLeft
    );

    if (!rightSection && !leftSection) return "";

    return !rightSection ||
      (leftSection?.order === 0 && !leftSection?.completionDate) ||
      (rightSection?.order === 0 && rightSection?.completionDate)
      ? eyeLeft
      : eyeRight;
  }, [exam, currentExamSection, eyeRight, eyeLeft]);

  // the data for the number chart (for visual field), or esterman chart (for esterman)
  const visualFieldChartData = useMemo(() => {
    // disconsider if chart is not visual field/esterman
    if (!(isVisualField || isEsterman)) return null;
    return getChartSpots(examReportLive);
  }, [examReportLive, isVisualField, isEsterman]);

  // the data for the gaze chart
  const gazeChartData = useMemo(() => {
    // disconsider if chart is not visual field/esterman
    if (!(isVisualField || isEsterman)) return null;
    const { gazeSpots, eyeTrackingError } = getGazeChartData(examReportLive);

    setshouldDisplayEyeTrackingError(
      isVisualField &&
        !isRightCompleted &&
        examReportLive &&
        gazeSpots?.length >= 10 &&
        eyeTrackingError
    );
    return gazeSpots;
  }, [isVisualField, isEsterman, examReportLive, isRightCompleted]);

  const showVisualFieldChart = useMemo(
    () =>
      isVisualField &&
      examReportLive &&
      exam &&
      !isConfirmation &&
      !isNotStarted &&
      !inTutorial &&
      !inCalibration,
    [
      exam,
      examReportLive,
      inCalibration,
      inTutorial,
      isConfirmation,
      isNotStarted,
      isVisualField,
    ]
  );

  const showEstermanChart = useMemo(
    () =>
      isEsterman &&
      examReportLive &&
      exam &&
      !isConfirmation &&
      !isNotStarted &&
      !inTutorial &&
      !inCalibration,
    [
      exam,
      examReportLive,
      inCalibration,
      inTutorial,
      isConfirmation,
      isNotStarted,
      isEsterman,
    ]
  );

  const showNonVisualChartLegend = useMemo(
    () =>
      (isPelirobson ||
        isTumbling ||
        isVisualAcuity ||
        isLandoltC ||
        isBaileyLovie ||
        isSnellen) &&
      examReportLive,
    [
      isPelirobson,
      isTumbling,
      isVisualAcuity,
      isLandoltC,
      isSnellen,
      isBaileyLovie,
      examReportLive,
    ]
  );

  const showNoDataAvailable = useMemo(
    () => !examReportLive && (!isPaused || isConfirmation) && !inCalibration,
    [examReportLive, isPaused, isConfirmation, inCalibration]
  );

  const showPaused = useMemo(
    () => !examReportLive && isPaused,
    [examReportLive, isPaused]
  );

  const showHidingChart = useMemo(
    () => inTutorial || inCalibration,
    [inTutorial, inCalibration]
  );

  return isVrMode ? (
    <Box pt={2}>
      <WebRtcComponent connection={connection} />
    </Box>
  ) : (
    <Box
      display="flex"
      flexDirection="column"
      padding="16px"
      gap="20px"
      sx={{ border: "1px solid #5B6D7A40", borderRadius: "8px" }}
    >
      {showPaused || showNoDataAvailable || showHidingChart ? (
        // Show info / error text if the graph(s) can't be displayed
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          p="20px 0px"
          gap="20px"
        >
          {showPaused && (
            <Typography fontSize="16px">{`${t("currently_paused")}.`}</Typography>
          )}
          {showNoDataAvailable && (
            <Typography fontSize="16px">
              {`${t("no_data_available")}.`}
            </Typography>
          )}
          {showHidingChart && (
            <Typography fontSize="16px">
              {t("tutorial_hiding_contents_alert")}
            </Typography>
          )}
        </Box>
      ) : isVisualField || isEsterman ? (
        // Show visual field graphs
        <Box display="flex" flexDirection="column" gap="20px">
          <Typography fontSize="16px" fontWeight={600}>
            {t("eye_tracking")}
          </Typography>
          {!isConfirmation && !isNotStarted && (
            <GazeGraph data={gazeChartData || []} />
          )}
          <Typography fontSize="16px" fontWeight={600}>
            {t("word_grid")}
          </Typography>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap="20px"
          >
            {/* For visual field (that are not single threshold), we show the number graph */}
            {showVisualFieldChart && !isSingleThreshold && (
              <NumberGraph
                pressedPoints={visualFieldChartData || []}
                gridType={currentExamSection?.gridType}
                shouldDisplayTriangle={
                  currentExamSection?.gridType !== "G_10_2"
                }
                isNotRlFast={currentExamSection?.algorithm !== "SCREEN_FAST"}
                isLiveUpdate={true}
                eyeLiveUpdate={eye}
              />
            )}
            {/* For visual field (that are single threshold), we show the single threshold graph */}
            {showVisualFieldChart && isSingleThreshold && (
              <SingleThresholdGraph
                pressedPoints={visualFieldChartData || []}
                tickNum={6}
              />
            )}
            {/* For esterman, we show the esterman graph */}
            {showEstermanChart && (
              <EstermanGraph
                pressedPoints={visualFieldChartData || []}
                gridType={currentExamSection?.gridType}
                isLiveUpdate={true}
              />
            )}
            <Box
              width="100%"
              display="flex"
              gap="16px"
              justifyContent="space-around"
            >
              <Box display="flex" gap={1}>
                <Typography fontSize="14px">{`${t("false_positive")} (${t("external_exam_details_FP")}): ${falsePositivePercentage}`}</Typography>
              </Box>
              <Box display="flex" gap={1}>
                <Typography fontSize="14px">{`${t("false_negative")} (${t("external_exam_details_FN")}): ${falseNegative}`}</Typography>
              </Box>
              <Box display="flex" gap={1}>
                <Typography fontSize="14px">{`${t("fixation_loss")} (FL): ${fixationLossPercentage}`}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        // Show non-visual field graphs
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          gap="20px"
        >
          {isSynoptophore && examReportLive && !inTutorial && (
            <Synoptophore examData={examReportLive} />
          )}
          {isColorVision && examReportLive && !inTutorial && (
            <ColorVisionGraph data={examReportLive} isLiveUpdate={true} />
          )}
          {showNonVisualChartLegend && (
            <Grid item xs={12} pb={1}>
              <ChartLegendExceptVisualField isLiveUpdate={true} />
            </Grid>
          )}
          {isPelirobson && examReportLive && !inTutorial && (
            <PelliRobson examData={examReportLive} />
          )}
          {isTumbling && examReportLive && !inTutorial && (
            <TumblingE examData={examReportLive} />
          )}
          {isTumbling && isVisualAcuity && examReportLive && (
            <VisualAcuityGraph examData={examReportLive} isLiveUpdate={true} />
          )}
          {isLandoltC && examReportLive && (
            <VisualAcuityGraph
              examData={examReportLive}
              isLandoltC={isLandoltC}
              // isLiveUpdate={true}
            />
          )}
          {isSnellen && examReportLive && (
            <VisualAcuityGraph
              examData={examReportLive}
              isSnellen={isSnellen}
              isLiveUpdate={true}
            />
          )}
          {isBaileyLovie && examReportLive && (
            <BaileyLovieGraph examData={examReportLive} />
          )}
          {isRAPD && examReportLive && (
            <>
              <RAPDChartLegend />
              <RAPDChart chartData={examReportLive?.state} isLiveUpdate />
              <RAPDPupils chartData={examReportLive?.state} />
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default LiveUpdateGraph;
